// =============================================================================
// Design tokens + MUI theme
// -----------------------------------------------------------------------------
// The numbered ramps (grey/primary/secondary 100–900) are consumed directly by
// many components as theme.palette.<ramp>[nnn], so they are preserved verbatim
// for backward-compat. The enterprise uplift is layered on top via corrected
// semantic backgrounds (incl. the previously-undefined background.secondary),
// real light/dark palettes, and global component overrides.
// =============================================================================

// color design tokens export
export const tokensDark = {
  grey: {
    0: "#ffffff", // manually adjusted
    10: "#f6f6f6", // manually adjusted
    50: "#f0f0f0", // manually adjusted
    100: "#e0e0e0",
    200: "#c2c2c2",
    300: "#a3a3a3",
    400: "#858585",
    500: "#666666",
    600: "#525252",
    700: "#3d3d3d",
    800: "#292929",
    900: "#141414",
    1000: "#000000", // manually adjusted
  },
  primary: {
    // indigo / brand
    100: "#e0e1fb",
    200: "#c2c4f7",
    300: "#a3a6f0",
    400: "#8488ea",
    500: "#6366f1", // accent on dark
    600: "#4f46e5", // main brand
    700: "#4338ca",
    800: "#312e94",
    900: "#1e1b57",
  },
  secondary: {
    // teal / emerald accent (replaces dated gold)
    50: "#eafaf4",
    100: "#c9f2e2",
    200: "#96e6c8",
    300: "#5fd6ab",
    400: "#2fc191",
    500: "#14a97b",
    600: "#0f8a66",
    700: "#0c6d51",
    800: "#0a5340",
    900: "#073a2d",
  },
};

// function that reverses the color palette
function reverseTokens(tokensDark) {
  const reversedTokens = {};
  Object.entries(tokensDark).forEach(([key, val]) => {
    const keys = Object.keys(val);
    const values = Object.values(val);
    const length = keys.length;
    const reversedObj = {};
    for (let i = 0; i < length; i++) {
      reversedObj[keys[i]] = values[length - i - 1];
    }
    reversedTokens[key] = reversedObj;
  });
  return reversedTokens;
}
export const tokensLight = reverseTokens(tokensDark);

// Semantic status colors used by StatusChip and tables across the app.
export const statusPalette = {
  pending: "#f59e0b",
  confirmed: "#3b82f6",
  processing: "#6366f1",
  shipped: "#8b5cf6",
  delivered: "#10b981",
  completed: "#10b981",
  cancelled: "#ef4444",
  failed: "#ef4444",
  paid: "#10b981",
  partial: "#f59e0b",
  unpaid: "#ef4444",
  refunded: "#8b5cf6",
  active: "#10b981",
  inactive: "#94a3b8",
  // Support-ticket priority + status
  open: "#3b82f6",
  closed: "#94a3b8",
  resolved: "#10b981",
  high: "#ef4444",
  medium: "#f59e0b",
  low: "#10b981",
};

// mui theme settings
export const themeSettings = (mode) => {
  const isDark = mode === "dark";

  return {
    palette: {
      mode: mode,
      ...(isDark
        ? {
            // ── dark mode ──────────────────────────────────────────────────
            primary: {
              ...tokensDark.primary,
              main: tokensDark.primary[500],
              light: tokensDark.primary[400],
              dark: tokensDark.primary[700],
              contrastText: "#ffffff",
            },
            secondary: {
              ...tokensDark.secondary,
              main: tokensDark.secondary[400],
            },
            neutral: {
              ...tokensDark.grey,
              main: tokensDark.grey[500],
            },
            background: {
              default: "#0b0f17",
              paper: "#141926",
              alt: "#161c2b",
              secondary: "#1e2536",
            },
            divider: "rgba(148,163,184,0.14)",
            text: {
              primary: "#e8eaf0",
              secondary: "#98a2b3",
            },
          }
        : {
            // ── light mode ─────────────────────────────────────────────────
            primary: {
              ...tokensDark.primary,
              main: tokensDark.primary[600],
              light: tokensDark.primary[400],
              dark: tokensDark.primary[800],
              contrastText: "#ffffff",
            },
            secondary: {
              ...tokensDark.secondary,
              main: tokensDark.secondary[600],
              light: tokensDark.secondary[400],
            },
            neutral: {
              ...tokensLight.grey,
              main: tokensDark.grey[500],
            },
            background: {
              default: "#f6f7f9",
              paper: "#ffffff",
              alt: "#ffffff",
              secondary: "#f1f3f6",
            },
            divider: "rgba(15,23,42,0.09)",
            text: {
              primary: "#111827",
              secondary: "#5b6472",
            },
          }),
      success: { main: statusPalette.delivered },
      warning: { main: statusPalette.pending },
      error: { main: statusPalette.cancelled },
      info: { main: statusPalette.confirmed },
    },
    // Base unit for controls (buttons, inputs, chips). Surfaces (cards,
    // tables) use 12px via component overrides. Keeping this modest avoids
    // MUI's sx `borderRadius` multiplier blowing legacy values out of scale.
    shape: {
      borderRadius: 8,
    },
    typography: {
      fontFamily: ["Inter", "sans-serif"].join(","),
      fontSize: 13,
      h1: { fontFamily: "Inter, sans-serif", fontSize: 40, fontWeight: 700 },
      h2: { fontFamily: "Inter, sans-serif", fontSize: 30, fontWeight: 700 },
      h3: { fontFamily: "Inter, sans-serif", fontSize: 24, fontWeight: 600 },
      h4: { fontFamily: "Inter, sans-serif", fontSize: 20, fontWeight: 600 },
      h5: { fontFamily: "Inter, sans-serif", fontSize: 16, fontWeight: 600 },
      h6: { fontFamily: "Inter, sans-serif", fontSize: 14, fontWeight: 600 },
      button: { textTransform: "none", fontWeight: 600 },
    },
    components: {
      MuiCssBaseline: {
        styleOverrides: {
          body: { scrollbarWidth: "thin" },
        },
      },
      MuiPaper: {
        styleOverrides: {
          root: { backgroundImage: "none" },
        },
      },
      MuiCard: {
        defaultProps: { elevation: 0 },
        styleOverrides: {
          root: ({ theme }) => ({
            borderRadius: 12,
            border: `1px solid ${theme.palette.divider}`,
            backgroundColor: theme.palette.background.alt,
          }),
        },
      },
      MuiButton: {
        defaultProps: { disableElevation: true },
        styleOverrides: {
          root: { borderRadius: 8, textTransform: "none" },
        },
      },
      MuiInputLabel: {
        styleOverrides: {
          root: ({ theme }) => ({
            color: theme.palette.text.secondary,
            "&.Mui-focused": { color: theme.palette.primary.main },
          }),
        },
      },
      MuiOutlinedInput: {
        styleOverrides: {
          root: { borderRadius: 8 },
        },
      },
      MuiTableCell: {
        styleOverrides: {
          head: ({ theme }) => ({
            fontWeight: 700,
            color: theme.palette.text.secondary,
            backgroundColor: theme.palette.background.secondary,
            borderBottom: `1px solid ${theme.palette.divider}`,
            fontSize: 12,
            textTransform: "uppercase",
            letterSpacing: 0.4,
          }),
          root: ({ theme }) => ({
            borderBottom: `1px solid ${theme.palette.divider}`,
          }),
        },
      },
      MuiChip: {
        styleOverrides: {
          root: { fontWeight: 600 },
        },
      },
      MuiGrid: {
        defaultProps: {
          unstable_strictMode: true, // Enable Grid v2
        },
      },
    },
  };
};
